import { useCallback, useEffect, useState } from 'react';
import {
  createDraftFromVersion,
  publishVersion,
  archiveVersion as archiveVersionUtil,
  newId,
} from '@fulcrum/form-embedded';
import type { FormDefinition, FormVersion } from '@fulcrum/form-embedded';
import { SEED_DEFINITIONS, SEED_VERSIONS } from './seedTemplates';

const STORAGE_KEY = 'fulcrum-form-host-v1';

interface StoredState {
  definitions: FormDefinition[];
  versions: FormVersion[];
}

function loadInitial(): StoredState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as StoredState;
  } catch {
    // fall through to seed
  }
  return { definitions: SEED_DEFINITIONS, versions: SEED_VERSIONS };
}

/**
 * localStorage-backed mock persistence layer standing in for a real production
 * backend. Demonstrates the exact callback contract a real host must
 * implement — see FormBuilderCallbacks in @fulcrum/form-embedded.
 */
export function useFormStore() {
  const [state, setState] = useState<StoredState>(loadInitial);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const saveDraft = useCallback((version: FormVersion) => {
    setState((s) => ({
      ...s,
      versions: s.versions.map((v) => (v.formVersionId === version.formVersionId ? version : v)),
    }));
  }, []);

  const publish = useCallback((version: FormVersion) => {
    setState((s) => ({
      ...s,
      versions: s.versions.map((v) => {
        if (v.formVersionId === version.formVersionId) return publishVersion(v, 'demo-user');
        if (v.formId === version.formId && v.status === 'published') return archiveVersionUtil(v, 'demo-user');
        return v;
      }),
      definitions: s.definitions.map((d) =>
        d.formId === version.formId ? { ...d, isActive: true, activeVersionId: version.formVersionId } : d,
      ),
    }));
  }, []);

  const createVersion = useCallback((fromVersionId: string) => {
    setState((s) => {
      const source = s.versions.find((v) => v.formVersionId === fromVersionId);
      if (!source) return s;
      const draft = createDraftFromVersion(source, s.versions, 'demo-user');
      return { ...s, versions: [...s.versions, draft] };
    });
  }, []);

  const restoreVersion = useCallback((versionId: string) => {
    setState((s) => {
      const source = s.versions.find((v) => v.formVersionId === versionId);
      if (!source) return s;
      const draft = createDraftFromVersion(source, s.versions, 'demo-user');
      return { ...s, versions: [...s.versions, draft] };
    });
  }, []);

  const archiveVersion = useCallback((versionId: string) => {
    setState((s) => ({
      ...s,
      versions: s.versions.map((v) => (v.formVersionId === versionId ? archiveVersionUtil(v, 'demo-user') : v)),
    }));
  }, []);

  const updateDefinition = useCallback((formId: string, patch: Partial<FormDefinition>) => {
    setState((s) => ({
      ...s,
      definitions: s.definitions.map((d) =>
        d.formId === formId ? { ...d, ...patch, updatedAt: new Date().toISOString(), updatedBy: 'demo-user' } : d,
      ),
    }));
  }, []);

  const createForm = useCallback((name: string) => {
    const formId = newId();
    const versionId = newId();
    const now = new Date().toISOString();
    const definition: FormDefinition = {
      formId,
      name,
      isActive: false,
      isLocked: false,
      flags: {},
      createdAt: now,
    };
    const version: FormVersion = {
      formVersionId: versionId,
      formId,
      versionNumber: 1,
      status: 'draft',
      document: { sections: [] },
      createdAt: now,
      createdBy: 'demo-user',
    };
    setState((s) => ({ definitions: [...s.definitions, definition], versions: [...s.versions, version] }));
    return { definition, version };
  }, []);

  const deleteForm = useCallback((formId: string) => {
    setState((s) => ({
      definitions: s.definitions.filter((d) => d.formId !== formId),
      versions: s.versions.filter((v) => v.formId !== formId),
    }));
  }, []);

  function latestVersionFor(formId: string): FormVersion {
    const forForm = state.versions.filter((v) => v.formId === formId);
    return [...forForm].sort((a, b) => b.versionNumber - a.versionNumber)[0];
  }

  return {
    definitions: state.definitions,
    versions: state.versions,
    latestVersionFor,
    saveDraft,
    publish,
    createVersion,
    restoreVersion,
    archiveVersion,
    createForm,
    updateDefinition,
    deleteForm,
  };
}
